import React, { useState } from 'react';
import { Palette, CheckCircle, X, Eye } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { translations, products } from '../data/mock';

export const Products = () => {
  const { language } = useLanguage();
  const t = translations[language].products;
  const [selectedImage, setSelectedImage] = useState(null);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="products" className="py-24 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl sm:text-5xl font-black text-slate-800 mb-4">
            {t.title}{' '}
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
              {t.titleGradient}
            </span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
          {products.map((product, index) => (
            <Card
              key={product.id}
              className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-slate-200"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-72 overflow-hidden bg-slate-100">
                <img
                  src={product.image}
                  alt={product.type}
                  className="w-full h-full object-cover"
                />
                {/* Hover Overlay with View Button */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button
                    onClick={() => setSelectedImage(product)}
                    className="bg-white text-slate-800 hover:bg-slate-100 shadow-xl transform scale-95 group-hover:scale-100 transition-transform duration-300"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'View' : 'Lihat'}
                  </Button>
                </div>
              </div>

              <CardContent className="p-5">
                {/* Product Type */}
                <h3 className="font-bold text-lg text-slate-800 mb-4 line-clamp-2 min-h-[56px]">
                  {product.type}
                </h3>

                {/* B2B Information */}
                <div className="space-y-3">
                  {/* Material Options */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-2">{t.card.material}:</p>
                    <div className="flex flex-wrap gap-1">
                      {product.materials.map((material, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs bg-slate-100 text-slate-700">
                          {material}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Production Method */}
                  <div className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-600">{t.card.production}:</span>
                    <span className="font-semibold text-slate-800">{product.production}</span>
                  </div>

                  {/* MOQ */}
                  <div className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-600">{t.card.moq}:</span>
                    <span className="font-semibold text-blue-600">{product.moq}</span>
                  </div>

                  {/* Custom Design & Export Ready */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-slate-600">{t.card.custom}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-slate-600">{t.card.export}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            size="lg"
            className="px-8 py-6 text-lg bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            onClick={scrollToContact}
          >
            <Palette className="w-5 h-5 mr-2" />
            {t.cta}
          </Button>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-5xl max-h-[90vh] relative">
            <img
              src={selectedImage.image}
              alt={selectedImage.type}
              className="w-full h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
              <h3 className="text-white text-2xl font-bold mb-2">{selectedImage.type}</h3>
              <div className="flex flex-wrap gap-2">
                {selectedImage.materials.map((material, idx) => (
                  <Badge key={idx} className="bg-white/20 text-white border-white/30">
                    {material}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
